<script lang="ts">
  import { onMount } from "svelte";
  import { writable, Writable, Readable, get } from "svelte/store";
  import { clamp } from "../scripts/utils";
  import type {
    WinStyle,
    WinProps,
    WinCbs,
    Rect,
    Theme,
  } from "../scripts/WinTypes";
  import ProcMgr, { WinProc } from "../stores/ProcMgr";
  import { addError, addLog } from "../stores/LoggerStore";
  import Loading from "./Loading.svelte";
  import SettingsMgr from "../stores/SettingsMgr";

  export let winProps: WinProps = undefined;
  const thisWindowId = winProps.id;
  export let callbacks: WinCbs = undefined;

  const procmgr = ProcMgr.getInstance();
  const thememgr = SettingsMgr.getInstance();
  let _theme = thememgr.theme;
  const themeWatcher = (theme: Theme) => {
    if (!closeBtnElem) {
      return;
    }
    const c = theme.colors;

    closeBtnElem.style.setProperty("background-color", c.error);
    minBtnElem.style.setProperty("background-color", c.warn);
    maxBtnElem.style.setProperty("background-color", c.okay);
    winElem.style.setProperty("color", c["1"]);
    winElem.style.setProperty("background-color", c["2"]);
    navElem.style.setProperty("color", c.navcolor || c["2"]);
    navElem.style.setProperty("background-color", c.navback || c["1"]);
    const _set = (prop, ett, defaultValue = "none", elem = winElem) => {
      if (ett) {
        elem.style.setProperty(prop, ett);
      } else {
        elem.style.setProperty(prop, defaultValue);
      }
    };

    _set("opacity", c.opacity, "1");
    _set("border", c.winborder, "none");
    _set("box-shadow", c.winshadow, "none");
    _set("backdrop-filter", c.winfilter, "none");
    _set("mix-blend-mode", c.navmix, "normal", navElem);
    _set("text-shadow", c.titleshadow, "none", titleElem);

    return c;
  };
  $: _colors = themeWatcher($_theme);

  let _procCount = procmgr.procCount;
  const calcInitLeft = (count: number) => {
    return `${3 + Math.floor(count / 20) + (count % 20) * 2}%`;
  };
  const calcInitTop = (count: number) => {
    return `${5 + 3 * Math.floor(count / 20) + (count % 20) * 2}%`;
  };
  let initLeft = calcInitLeft($_procCount);
  let initTop = calcInitTop($_procCount);
  const initialRect: Rect = {
    top: initTop,
    left: initLeft,
    width: "50%",
    height: "70%",
  };
  const mergeRect = () => {
    if (!winProps.data) {
      winProps.data = {};
    }
    if (!winProps.data["rect"]) {
      winProps.data["rect"] = { ...initialRect };
    } else {
      for (let prop in initialRect) {
        winProps.data["rect"][prop] =
          winProps.data["rect"][prop] || initialRect[prop];
      }
    }
    console.log(`${winProps.name}:rect:`, winProps.data["rect"]);
    return winProps.data["rect"];
  };
  let _prevRect = writable(<Rect>{});
  class _PrevRectUpdater {
    public rect(k, v) {
      _prevRect.update((rect) => {
        rect[k] = v;
        return rect;
      });
      return this;
    }
    get(k) {
      return get(_prevRect)[k];
    }
  }
  let prevRect = new _PrevRectUpdater();
  let currentRect: Writable<Rect> = writable({ ...mergeRect() });

  const paramSetter = (param: string, value: any) => {
    console.log(`Window. Setting ${param} to ${value}`);
    _callbackParams.update((params) => {
      const retval = { ...params };

      if (param in retval) {
        retval[param].update((_) => value);
      } else {
        addError(
          `No such window callback parameter. Parameter : ${param}, Value : ${value}`
        );
      }
      return retval;
    });
  };
  const _callbackParams = writable({
    loading: writable(false),
    fadeOutOnDestroy: writable(false),
    maximizeOnMount: writable(false),
  });
  let loading = $_callbackParams.loading;

  class _Updater {
    public rect(k, v) {
      currentRect.update((rect) => {
        rect[k] = v;
        return rect;
      });
      return this;
    }
  }
  const Updater = new _Updater();

  procmgr.setWinDataFecther(winProps.id, "rect", () => currentRect);
  procmgr.setWinDataFecther(winProps.id, "isMaximized", () => isMaximized);

  let winTitle = winProps?.name || "Application";

  //bindings
  let winElem: HTMLElement,
    navElem: HTMLElement,
    contentElem: HTMLElement,
    titleElem: HTMLElement;
  let winElemClientHeight; // bind:clientHeight creates an empty iframe inside...
  let pos1 = 0,
    pos2 = 0,
    pos3 = 0,
    pos4 = 0;
  const maximizeTransition = "0.3s";
  let width, height, top, left;
  let isMaximized = writable(false);

  //disable drag on maximize
  const setWindowDragEvent = (isMax: boolean) => {
    if (!navElem) {
      return;
    }
    navElem.onmousedown = isMax ? undefined : dragMouseDown;
    navElem.classList.toggle("grabbable", !isMax);
    return "";
  };
  $: dragWatcher = setWindowDragEvent($isMaximized);

  function dragMouseDown(e) {
    e = e || window.event;
    e.preventDefault();
    // get the mouse cursor position at startup:
    pos3 = e.clientX;
    pos4 = e.clientY;
    winElem.style.transition = "0s";
    contentElem.classList.add("dragging");
    document.onmouseup = closeDragElement;
    // call a function whenever the cursor moves:
    document.onmousemove = elementDrag;
  }

  function elementDrag(e) {
    e = e || window.event;
    e.preventDefault();

    //remove window transition

    // calculate the new cursor position:
    pos1 = pos3 - e.clientX;
    pos2 = pos4 - e.clientY;
    pos3 = e.clientX;
    pos4 = e.clientY;

    // set the element's new position and bound:
    const minHeight = 150;
    const minWidth = 180;
    let offsetTop = clamp(
      winElem.offsetTop - pos2,
      0,
      window.innerHeight - minHeight
    );
    let offsetLeft = clamp(
      winElem.offsetLeft - pos1,
      minWidth - parseInt(getComputedStyle(winElem).width.replace("px", "")),
      window.innerWidth - minWidth
    );
    winElem.style.top = `${offsetTop}px`;
    winElem.style.left = `${offsetLeft}px`;
    Updater.rect("top", `${offsetTop}px`);
    Updater.rect("left", `${offsetLeft}px`);
  }

  function closeDragElement() {
    /* stop moving when mouse button is released:*/
    document.onmouseup = null;
    document.onmousemove = null;

    {
      //for store update
      Updater.rect("top", winElem.style.top).rect("left", winElem.style.left);
    }
    //restore transition
    // winElem.style.transition = maximizeTransition;
    // win.style.transition = transition;
    contentElem.classList.remove("dragging");
  }

  const onCloseBtn = () => {
    const closeImmediately = !true;
    if (closeImmediately) {
      procmgr.kill(winProps.id);
    } else {
      if (callbacks?.confirmClose && !callbacks?.confirmClose?.()) {
        addLog("close not confirmed");
        return;
      }
      winElem.style.transition = "0.1s";
      winElem.style.opacity = "0";
      winElem.style.width = "0%";
      winElem.style.height = "0%";
      // winElem.style.transform = "translate(0%, 0%)";
      setTimeout(() => {
        procmgr.kill(winProps.id);
      }, 150);
    }
  };

  const maximizeRect = () => {
    const curRect = getComputedStyle(winElem);
    prevRect.rect("width", curRect.width);
    prevRect.rect("height", curRect.height);
    prevRect.rect("top", curRect.top);
    prevRect.rect("left", curRect.left);

    winElem.style.transition = maximizeTransition;
    winElem.style.width = "100%";
    winElem.style.height = "100%";
    winElem.style.top = "0px";
    winElem.style.left = "0px";

    //additional style
    winElem.style.borderRadius = "0px";

    isMaximized.update((val) => true);
    // isMaximized = true;
  };
  const restoreRect = () => {
    winElem.style.width = prevRect.get("width");
    winElem.style.height = prevRect.get("height");
    winElem.style.top = prevRect.get("top");
    winElem.style.left = prevRect.get("left");
    winElem.style.borderRadius = "8px";
    isMaximized.update((val) => false);
    // isMaximized = false;
  };
  const onMaximizeBtn = () => {
    if ($isMaximized) {
      restoreRect();
    } else {
      maximizeRect();
    }
  };
  const watchMaximizeOnMount = (doMax) => {
    if (doMax) {
      addLog("Maxmimze on mount " + winProps?.name);
      maximizeRect();
      setWindowDragEvent($isMaximized);
    }
    return "";
  };
  let maximizeOnMount = $_callbackParams.maximizeOnMount;
  $: maximizeWatcher = watchMaximizeOnMount($maximizeOnMount);

  let closeBtnElem: HTMLElement,
    minBtnElem: HTMLElement,
    maxBtnElem: HTMLElement;

  onMount(() => {
    _colors = themeWatcher($_theme);
    callbacks?.onMount?.(paramSetter);
    setWindowDragEvent($isMaximized);

    {
      const e = winElem as HTMLElement;

      const s = winProps.data?.rect || get(currentRect);
      e.style.width = s.width;
      if (s.aspectRatio) {
        e.style.aspectRatio = `${s.aspectRatio} / 1`;
      } else {
        e.style.height = s.height;
      }
      e.style.top = s.top;
      e.style.left = s.left;
      if (winProps.data?.rect) {
        const r = winProps.data?.rect;
        Updater.rect("top", r.top);
        Updater.rect("left", r.left);
        Updater.rect("width", r.width);
        Updater.rect("height", r.height);
      }
    }
    setActive();

    if (winProps["maximizeOnMount"]) {
      console.log("Maximize on mount");
      onMaximizeBtn();
    }
  });

  const setActive = () => {
    // console.log("Set active called");
    procmgr.setActiveWindow(winProps);
    callbacks?.onFocus?.();
  };
  const handleContentElemHeight = (h: number) => {
    if (!contentElem) {
      return;
    }
    contentElem.style.height = `${h - 30}px`;
  };
  $: contentElemHeight = handleContentElemHeight(winElemClientHeight);

  //register callbacks
  const setWindowRect = (rect) => {
    //{top, left, width, height}
    for (let key in rect) {
      Updater.rect(key, rect[key]);
    }
    callbacks?.onWindowRectFinish();
  };
  const setWindowPrevRect = (rect) => {
    //{top, left, width, height}
    for (let key in rect) {
      prevRect.rect(key, rect[key]);
    }
  };
  const watchRect = (rect: Rect) => {
    if (!winElem) {
      return;
    }
    winElem.style.width = rect.width;
    winElem.style.height = rect.height;
    winElem.style.top = rect.top;
    winElem.style.left = rect.left;
    // console.log("watchRect, rect : ", rect);
  };
  $: rectWatcher = watchRect($currentRect);
  callbacks?.setWindowRect?.(setWindowRect);
  callbacks?.setWindowPrevRect?.(setWindowPrevRect);
  callbacks?.closeWindow?.(onCloseBtn);
  callbacks?.maximizeWindow?.(onMaximizeBtn);
  const watchMaximize = (isMax) => {
    callbacks?.onMaximizeStart?.();
    const transitionFinished =
      10 + Math.floor(parseFloat(maximizeTransition.replace("s", "")) * 1000);
    setTimeout(() => {
      callbacks?.onMaximizeFinish?.();
    }, transitionFinished);
  };
  $: _max = watchMaximize($isMaximized);
</script>

<!-- <section
  class="window-container"
  bind:this={winElem}
  on:mousedown={setActive}
  bind:clientHeight={winElemClientHeight}
> -->
<section class="window-container" bind:this={winElem} on:mousedown={setActive}>
  {#if $loading}
    <Loading />
  {/if}
  <div class="window-container-header grabbable" bind:this={navElem}>
    <ul class="button-container">
      <li class="btn close" bind:this={closeBtnElem} on:click={onCloseBtn} />
      <li class="btn minimize" bind:this={minBtnElem} />
      <li
        class="btn maximize"
        bind:this={maxBtnElem}
        on:click={onMaximizeBtn}
      />
    </ul>
    <span class="window-title" bind:this={titleElem}>{winTitle}</span>
    <!-- <span class="proc-id">ProcID:{winProc.id}</span> -->
  </div>
  <div class="content-container" bind:this={contentElem}>
    <slot />
  </div>
</section>
<div class="css-keeper content-container dragging grabbable">
  <div class="css-keeper window-container" />
</div>

<style>
  .window-container {
    position: absolute;
    /* color: #dbdbdb; */
    border-radius: 8px;
    overflow: hidden;
    /* border: 1px solid #dbdbdb; */
    /* resize: both; */
    user-select: none;
  }
  .window-container-header {
    position: absolute;
    width: 100%;
    height: 30px;
    top: 0;
    left: 0;
    background-color: #3c3a38;
  }
  .window-title {
    font-weight: 500;
    font-size: 1.1rem;
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  }
  .content-container {
    position: absolute;
    top: 30px;
    left: 0px;
    width: 100%;
    height: calc(100% - 30px);
    box-sizing: border-box;
    overflow: auto;
  }

  .grabbable .window-container {
    transform: scale(0.8) translateY(-20%);
  }

  .content-container.dragging::after {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: #dbdbdb;
    opacity: 0.3;
  }

  .grabbable {
    cursor: move; /* fallback if grab cursor is unsupported */
    cursor: grab;
    cursor: -moz-grab;
    cursor: -webkit-grab;
  }
  .grabbable:active {
    cursor: grabbing;
    cursor: -moz-grabbing;
    cursor: -webkit-grabbing;
  }

  .button-container {
    padding: 0;
    margin: 0;
    list-style: none;
    display: flex;
    width: 60px;
    height: 100%;
    justify-content: space-evenly;
    align-items: center;
    position: absolute;
    top: 50%;
    left: 6px;
    transform: translateY(-50%);
    cursor: auto;
  }
  .proc-id {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    right: 0;
  }
  .button-container li.btn {
    /* flex: 1; */
    width: 10px;
    height: 10px;
    border-radius: 5px;
    cursor: pointer;
  }
  /* .btn.close {
    background-color: #ff605c;
  }
  .btn.minimize {
    background-color: #ffbd44;
  }
  .btn.maximize {
    background-color: #00ca4e;
  } */
  .css-keeper {
    display: none;
  }
</style>
